/**
 * useAgentConsoleFeed — the agent console's update engine (D-9, perf hardening).
 *
 * WHAT IT REPLACES. The console used to run `subscribeAgentEvents(refetch)`
 * where `refetch` fired the full seven-request fan on EVERY event, plus a blind
 * 30s interval that fired the same fan whether or not the tab was even on
 * screen. During a busy hour that is a fan per inbound text on top of two fans a
 * minute — the renderer freezes this package exists to kill.
 *
 * WHAT IT DOES INSTEAD — one engine with four inputs:
 *
 *   1. SSE events, coalesced leading-edge + trailing-edge. The first event fires
 *      a refetch immediately (a recruiter must see the new draft NOW). Every
 *      event that lands while that fetch is in flight, or inside the 2s window
 *      after it started, collapses into exactly ONE trailing refetch. The
 *      trailing edge is never dropped: it is retried from BOTH the window's
 *      expiry and the in-flight fetch's settle, whichever happens last.
 *   2. A 60s reconcile interval, running mount → unmount. This is the safety net
 *      for the failure mode the transport cannot report: a silently dead
 *      EventSource emits no error, and a missed frame leaves no trace. A stale
 *      console is worse than a slow one.
 *   3. document.hidden — reconcile ticks and events are SKIPPED while the tab is
 *      hidden (a background tab does not need the fan), each skip raising
 *      `missedWhileHidden`.
 *   4. visibilitychange → visible fires a refetch IMMEDIATELY and
 *      unconditionally. Waking a slept tab must never wait for the next tick,
 *      and it does not need to reason about what it missed.
 *
 * Everything is torn down on deps change and unmount: subscription, reconcile
 * interval, coalesce timer, visibility listener — plus a `disposed` latch so a
 * fetch that settles after teardown cannot schedule anything.
 */
import { useEffect, useRef, type MutableRefObject } from 'react';
import { subscribeAgentEvents } from '../lib/realtimeTransport';

/** Why a refetch ran. 'mount' is the effect's own first load (and a filter
 *  change's re-run) — the only one that must NOT bump the child refresh tick,
 *  matching the pre-D-9 effect exactly. */
export type FeedReason = 'mount' | 'event' | 'reconcile' | 'wake';

/** Leading-edge window: events inside it collapse into one trailing refetch. */
export const COALESCE_MS = 2_000;
/** Unconditional refetch cadence while the tab is visible. */
export const RECONCILE_MS = 60_000;

export interface AgentConsoleFeedOptions {
  coalesceMs?: number;
  reconcileMs?: number;
  /** Injectable for tests; defaults to the shared realtime transport. */
  subscribe?: (onEvent: () => void) => () => void;
  /** Injectable for tests; defaults to `document.hidden`. */
  isHidden?: () => boolean;
}

export interface AgentConsoleFeedState {
  /** Raised by every event/reconcile tick skipped because the tab was hidden;
   *  cleared by the unconditional wake refetch. Diagnostics only — correctness
   *  rides on the wake refetch, not on this flag. */
  missedWhileHidden: boolean;
}

export function useAgentConsoleFeed(
  onRefetch: (reason: FeedReason) => void | Promise<void>,
  options: AgentConsoleFeedOptions = {},
): MutableRefObject<AgentConsoleFeedState> {
  const {
    coalesceMs = COALESCE_MS,
    reconcileMs = RECONCILE_MS,
    subscribe = subscribeAgentEvents,
    isHidden,
  } = options;

  // Assigned during render, so it is current before any effect reads it: a
  // fetch that settles after a filter change re-runs against the NEW callback.
  const onRefetchRef = useRef(onRefetch);
  onRefetchRef.current = onRefetch;

  // Survives effect re-runs (a filter change must not forget that the tab was
  // hidden), which local `let`s inside the effect would not.
  const stateRef = useRef<AgentConsoleFeedState>({ missedWhileHidden: false });

  useEffect(() => {
    const state = stateRef.current;
    const hidden = () =>
      isHidden ? isHidden() : typeof document !== 'undefined' && document.hidden === true;

    let disposed = false;
    /** >0 while at least one refetch is outstanding. */
    let inFlight = 0;
    /** The 2s leading-edge window; non-null = we are inside it. */
    let windowTimer: ReturnType<typeof setTimeout> | null = null;
    /** An event arrived that no fetch has covered yet. */
    let trailing = false;

    const run = (reason: FeedReason) => {
      if (disposed) return;
      inFlight += 1;
      let settled = false;
      const settle = () => {
        if (settled) return;
        settled = true;
        inFlight -= 1;
        flush();
      };
      let result: void | Promise<void>;
      try {
        result = onRefetchRef.current(reason);
      } catch {
        settle(); // a throwing caller must not wedge the engine
        return;
      }
      if (result && typeof (result as Promise<void>).then === 'function') {
        (result as Promise<void>).then(settle, settle);
      } else {
        settle();
      }
    };

    const openWindow = () => {
      if (windowTimer !== null) clearTimeout(windowTimer);
      windowTimer = setTimeout(() => {
        windowTimer = null;
        flush();
      }, coalesceMs);
    };

    /** Fetch now AND open a fresh coalesce window around it (so a storm settles
     *  at one fetch per window instead of one per event). */
    const fire = (reason: FeedReason) => {
      openWindow();
      run(reason);
    };

    /**
     * Release an accumulated trailing edge as soon as it is legal to. Called
     * from the window's expiry AND from every fetch settling, so whichever is
     * last actually fires it — a final event can never be dropped.
     */
    function flush(): void {
      if (disposed || !trailing) return;
      if (inFlight > 0 || windowTimer !== null) return; // retried on settle / window end
      if (hidden()) {
        state.missedWhileHidden = true; // the wake refetch covers it
        return;
      }
      trailing = false;
      fire('event');
    }

    const onEvent = () => {
      if (disposed) return;
      if (hidden()) {
        state.missedWhileHidden = true;
        trailing = true; // belt: if the wake listener never fires, flush() still owes one
        return;
      }
      if (inFlight > 0 || windowTimer !== null) {
        trailing = true;
        return;
      }
      fire('event');
    };

    const onVisibility = () => {
      if (disposed || hidden()) return;
      // Unconditional, immediate: whatever we missed, this fetch supersedes it.
      trailing = false;
      state.missedWhileHidden = false;
      fire('wake');
    };

    const unsubscribe = subscribe(onEvent);
    run('mount');
    const reconcileId = setInterval(() => {
      if (disposed) return;
      if (hidden()) {
        state.missedWhileHidden = true;
        return;
      }
      run('reconcile');
    }, reconcileMs);
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', onVisibility);
    }

    return () => {
      disposed = true;
      unsubscribe();
      clearInterval(reconcileId);
      if (windowTimer !== null) {
        clearTimeout(windowTimer);
        windowTimer = null;
      }
      if (typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', onVisibility);
      }
    };
  }, [onRefetch, coalesceMs, reconcileMs, subscribe, isHidden]);

  return stateRef;
}

export default useAgentConsoleFeed;
